/**
 * ドラッグ操作（FR-007 / FR-010 / FR-011、`04-architecture.md` drag）。
 *
 * Pointer Events を `core` への操作へ変換する。タッチとマウスを同じコードパスで扱い、
 * HTML5 Drag and Drop API は使わない。
 *
 * - `pointerdown`: 掴んだピースが属するグループを特定する
 * - `pointermove`: グループをポインタに追従させ、ドロップ候補マスと予測の枠を更新する
 * - `pointerup`: `core/drop.resolveDrop()` を呼び、確定した配置を通知する
 *
 * **ドラッグ中は `core` の盤面を変更しない。**盤面が変わるのはドロップ確定の 1 回だけである。
 * ドロップ予測（赤枠 = 分解 / 白枠 = 維持）の判断は `core/drop.previewDrop()` に委ね、
 * ここでは候補マスから移動ベクトルを作って渡すだけで、壊れるかどうかの判断を自前に持たない。
 * 評価はドロップ候補マスが変わったときだけ行い、毎フレーム評価しない。
 */

import { positionsOf } from '../core/board';
import { type MoveVector, previewDrop, resolveDrop } from '../core/drop';
import { cellOf, col, inBoard, row } from '../core/grid';
import { computeGroups, groupOf } from '../core/groups';
import type { CellIndex, PieceId, Placement, TargetLayout } from '../core/types';
import type { DragLayer } from '../view/drag-layer';
import { toLogicalPoint } from '../view/layout';
import { pieceSize, setPiecePoint } from '../view/piece-view';
import { at } from './internal';

/** ドラッグが参照する盤面の現在状態。呼び出し側（shell / game）が握る。 */
export interface DragBoardState {
  readonly placement: Placement;
  readonly target: TargetLayout;
  readonly C: number;
  readonly R: number;
}

/** ドラッグコントローラの依存。DOM とゲーム状態の両方を注入で受け取る。 */
export interface DragControllerDeps {
  /** 盤面ラッパ要素。リスナーはすべてここに付ける。 */
  readonly board: HTMLElement;
  /** ピース ID から DOM 要素を引く（`board-view.pieceElement`）。 */
  readonly pieceElement: (piece: PieceId) => HTMLElement;
  /** 現在の拡大倍率（`view/layout.LayoutHandle.scale`）。 */
  readonly scale: () => number;
  /** 現在の盤面状態。ドラッグ開始時とドロップ確定時に読む。 */
  readonly state: () => DragBoardState;
  /** 候補マスと予測の枠の描画先。 */
  readonly layer: DragLayer;
  /**
   * ドロップ確定。新しい配置と、押しのけられたピース（FR-020 のアニメーション対象）を
   * 渡す。再描画は呼び出し側の責務。
   */
  readonly onDrop: (placement: Placement, displaced: PieceId[], dragged: PieceId[]) => void;
  /**
   * 入力を受け付けるか。段階遷移・クリア演出中は `false` を返す（FR-013 / FR-014、TK-013）。
   * 省略時は常に受け付ける。
   */
  readonly enabled?: () => boolean;
}

/** コントローラのハンドル。 */
export interface DragController {
  /** リスナーを外す。 */
  readonly dispose: () => void;
}

/** 進行中のドラッグ 1 回ぶんの状態。 */
interface DragSession {
  readonly pointerId: number;
  readonly capture: HTMLElement;
  readonly board: DragBoardState;
  readonly dragged: PieceId[];
  /** 各ドラッグピースの現在セル（`dragged` と同順）。 */
  readonly sourceCells: CellIndex[];
  readonly startX: number;
  readonly startY: number;
  /** 現在のドロップ候補の移動ベクトル。盤外なら `null`。 */
  candidate: MoveVector | null;
}

export function createDragController(deps: DragControllerDeps): DragController {
  let session: DragSession | null = null;

  const logicalPoint = (event: PointerEvent): { x: number; y: number } => {
    const rect = deps.board.getBoundingClientRect();
    return toLogicalPoint(event.clientX, event.clientY, rect, deps.scale());
  };

  const onPointerDown = (event: PointerEvent): void => {
    if (session !== null) return; // 2 本目以降の指は無視する
    if (deps.enabled && !deps.enabled()) return;
    if (event.button !== 0) return;
    const pieceRoot = (event.target as Element | null)?.closest<HTMLElement>('.piece');
    if (!pieceRoot?.dataset.piece) return;
    const scale = deps.scale();
    if (scale <= 0) return; // 倍率が未測定のうちは座標を論理系へ写せない

    const board = deps.state();
    const piece = Number(pieceRoot.dataset.piece);
    const groups = computeGroups(board.placement, board.target, board.C);
    const dragged = groupOf(groups, piece);
    const positionOf = positionsOf(board.placement);
    const sourceCells = dragged.map((p) => at(positionOf, p));

    const { x, y } = logicalPoint(event);
    session = {
      pointerId: event.pointerId,
      capture: pieceRoot,
      board,
      dragged,
      sourceCells,
      startX: x,
      startY: y,
      candidate: { dc: 0, dr: 0 },
    };

    // ポインタが盤面外へ出ても追従を維持する（FR-007）。テストの合成イベントでは
    // アクティブなポインタが存在せず例外になるため、失敗を許容する。
    try {
      pieceRoot.setPointerCapture(event.pointerId);
    } catch {
      /* 追従はリスナーが board に付いているため、キャプチャ無しでも盤面内では機能する */
    }

    for (const p of dragged) deps.pieceElement(p).classList.add('piece--dragging');
    // グループ影（TK-042）。形は掴んだ時点の footprint で確定し、以後は変位の追従だけ。
    deps.layer.beginShadow(sourceCells);
    updateOverlay(session, deps);
    event.preventDefault();
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (session === null || event.pointerId !== session.pointerId) return;
    const { x, y } = logicalPoint(event);
    const dx = x - session.startX;
    const dy = y - session.startY;
    const { C, R } = session.board;
    const size = pieceSize(C, R);

    // 追従。毎フレーム書くのは各ピースの --px / --py と、影 3 枚の transform だけ
    // （`04b` B-3 と同じ規律。TK-042 で影はピースの box-shadow からレイヤーへ移った）。
    for (const [i, p] of session.dragged.entries()) {
      const cell = at(session.sourceCells, i);
      setPiecePoint(deps.pieceElement(p), col(cell, C) * size.w + dx, row(cell, C) * size.h + dy);
    }
    deps.layer.moveShadow(dx, dy);

    // 候補マスの評価はマスが変わったときだけ。ポインタが同じマスの中で動いている間は
    // 結果が変わらない（`04-architecture.md`「ドロップの予測」）。
    const next = candidateVector(session, dx, dy, size.w, size.h);
    if (!sameCandidate(session.candidate, next)) {
      session.candidate = next;
      updateOverlay(session, deps);
    }
  };

  const finish = (event: PointerEvent, commit: boolean): void => {
    if (session === null || event.pointerId !== session.pointerId) return;
    const closing = session;
    session = null;

    try {
      closing.capture.releasePointerCapture(event.pointerId);
    } catch {
      /* キャプチャできていなければ解放も無い */
    }
    deps.layer.clear();
    for (const p of closing.dragged) {
      deps.pieceElement(p).classList.remove('piece--dragging', 'piece--oob');
    }

    const v = closing.candidate;
    if (commit && v !== null && (v.dc !== 0 || v.dr !== 0)) {
      const { placement, C, R } = closing.board;
      const result = resolveDrop(placement, closing.dragged, v, C, R);
      if (result.ok) {
        // 置いたピース自身も押しのけと同じ移動アニメーションで正規位置へ収める（TK-043）。
        // 持ち上がり（Z = ベベルの持ち上げ行と影）は piece--dragging の除去で既に瞬時に
        // 接地しており（B-7）、接地した高さのまま XY だけが滑る。
        deps.onDrop(
          result.placement,
          [...displacedPieces(placement, result.placement, closing.dragged), ...closing.dragged],
          [...closing.dragged],
        );
        return; // 再描画が全ピースの位置を確定させる
      }
    }
    // 盤外・移動量 0・中断は元の位置へ戻す（FR-007 / FR-010）。盤面は変わっていないため
    // 同じ placement を確定し直し、戻りの移動も同じアニメーション経路に載せる（TK-043）。
    deps.onDrop([...closing.board.placement], [...closing.dragged], [...closing.dragged]);
  };

  const onPointerUp = (event: PointerEvent): void => finish(event, true);
  const onPointerCancel = (event: PointerEvent): void => finish(event, false);

  deps.board.addEventListener('pointerdown', onPointerDown);
  deps.board.addEventListener('pointermove', onPointerMove);
  deps.board.addEventListener('pointerup', onPointerUp);
  deps.board.addEventListener('pointercancel', onPointerCancel);

  return {
    dispose: () => {
      deps.board.removeEventListener('pointerdown', onPointerDown);
      deps.board.removeEventListener('pointermove', onPointerMove);
      deps.board.removeEventListener('pointerup', onPointerUp);
      deps.board.removeEventListener('pointercancel', onPointerCancel);
    },
  };
}

/**
 * ポインタ変位からドロップ候補の移動ベクトルを作る。
 *
 * 最寄りのマスへ丸め、グループのどこかが盤外へはみ出すなら `null`（候補なし）。
 */
function candidateVector(
  session: DragSession,
  dx: number,
  dy: number,
  w: number,
  h: number,
): MoveVector | null {
  const v: MoveVector = { dc: Math.round(dx / w), dr: Math.round(dy / h) };
  const { C, R } = session.board;
  for (const cell of session.sourceCells) {
    if (!inBoard(col(cell, C) + v.dc, row(cell, C) + v.dr, C, R)) return null;
  }
  return v;
}

function sameCandidate(a: MoveVector | null, b: MoveVector | null): boolean {
  if (a === null || b === null) return a === b;
  return a.dc === b.dc && a.dr === b.dr;
}

/**
 * 候補マスのハイライトと予測の枠、盤外表示を現在の候補に合わせて描き直す。
 *
 * 盤外では `previewDrop` が両方とも空を返すため枠は消えるが、ハイライトも一緒に消し、
 * ドラッグ中のグループを半透明にして「ここには置けない」ことを示す（FR-007）。
 */
function updateOverlay(session: DragSession, deps: DragControllerDeps): void {
  const { placement, target, C, R } = session.board;
  const v = session.candidate;

  for (const p of session.dragged) {
    deps.pieceElement(p).classList.toggle('piece--oob', v === null);
  }
  if (v === null) {
    deps.layer.clear();
    return;
  }

  deps.layer.setCandidate(
    session.sourceCells.map((cell) => cellOf(col(cell, C) + v.dc, row(cell, C) + v.dr, C)),
  );

  const preview = previewDrop(placement, target, session.dragged, v, C, R);
  const positionOf = positionsOf(placement);
  const cellsOf = (groups: readonly (readonly PieceId[])[]): CellIndex[][] =>
    groups.map((members) => members.map((p) => at(positionOf, p)));
  deps.layer.setOutlines({
    decomposed: cellsOf(preview.decomposed),
    displaced: cellsOf(preview.displaced),
  });
}

/**
 * ドロップで押しのけられたピース = 位置が変わったピースのうち、ドラッグ対象でないもの。
 *
 * 押しのけアニメーション（FR-020、TK-012）の対象を決める。ドラッグされたグループは
 * ポインタから離した位置に既にいるため、補間の対象にしない。
 */
export function displacedPieces(
  before: Placement,
  after: Placement,
  dragged: readonly PieceId[],
): PieceId[] {
  const positionBefore = positionsOf(before);
  const positionAfter = positionsOf(after);
  const draggedSet = new Set(dragged);
  const displaced: PieceId[] = [];
  for (let piece = 0; piece < before.length; piece++) {
    if (draggedSet.has(piece)) continue;
    if (at(positionBefore, piece) !== at(positionAfter, piece)) displaced.push(piece);
  }
  return displaced;
}
